import {
  getStepVariableSources,
  getStepVariableBindingReadiness,
} from "@/domains/templates/step-variable-bindings";
import { isJsonPointer } from "@/domains/templates/subanalysis";
import { templateBuilderQueries } from "@/features/template-builder/queries";
import { TemplateVariableReferenceField } from "./template-variable-reference-field";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  TemplateInputFieldEditorRow,
  type TemplateInputFieldEditorData,
} from "@/domains/templates/components/template-input-fields/template-input-field-editor-row";
import {
  createInputFieldEditorRow,
  inputFieldEditorRowToDefinition,
  type InputFieldEditorRow,
} from "@/domains/templates/input-fields";
import { inputFieldDefinitionsToVariables } from "@/domains/templates/mutations";
import { templateQueries } from "@/domains/templates/queries";
import type {
  InputFieldDefinition,
  TemplateVariableInput,
  TemplateVariableTarget,
  Variable,
} from "@/domains/templates/types";
import { useTemplateVariableMutations } from "../hooks/use-template-variable-mutations";
import {
  saveVariableHierarchy,
  type VariableHierarchyFailure,
} from "../lib/save-variable-hierarchy";
import { getErrorMessage } from "@/shared/lib/error-message";

type VariableDialogForm = {
  field: unknown;
};

export type TemplateVariableDialogProps = {
  open: boolean;
  group?: "input" | "output";
  target: TemplateVariableTarget;
  variable?: Variable;
  variables: Variable[];
  selectableVariables: Variable[];
  editorData: TemplateInputFieldEditorData;
  loadChildren: (sourceVariableId: string) => Promise<Variable[]>;
  onOpenChange: (open: boolean) => void;
};

function variableDefinition(variable: Variable): InputFieldDefinition {
  return {
    key: variable.variable_name,
    type: variable.variable_type,
    description: variable.variable_description ?? undefined,
    source: variable.source_variable_id ? "variable" : "custom",
    variableId: variable.variable_id,
    sourceVariableId: variable.source_variable_id ?? undefined,
    variableNameFrontend: variable.variable_name_frontend,
    variableGroup: variable.variable_group,
    variableKind: variable.variable_kind,
    variableOptions: variable.variable_options,
    variableLinkedSchema: variable.variable_linked_schema,
    variableLinkedTable: variable.variable_linked_table,
    variableLinkedColumnId: variable.variable_linked_column_id,
    variableLinkedColumnName: variable.variable_linked_column_name,
    defaultValue: variable.variable_default,
    required: variable.variable_is_required,
    visible: variable.variable_is_visible,
    order: variable.variable_order,
    tier: variable.variable_tier,
    parentVariableId: variable.parent_variable_id,
  };
}

function toAtomicInput(
  definition: InputFieldDefinition,
): TemplateVariableInput {
  const input = (inputFieldDefinitionsToVariables([definition])[0] ??
    {}) as TemplateVariableInput & { variable_id?: string };
  const atomicInput = { ...input };
  delete atomicInput.variable_id;
  return atomicInput;
}

function canSave(field: InputFieldEditorRow): boolean {
  return field.source === "variable"
    ? Boolean(field.sourceVariableId)
    : Boolean(field.variableNameFrontend?.trim() || field.key.trim());
}

export function OrdinaryVariableDialog({
  open,
  group,
  target,
  variable,
  variables,
  selectableVariables,
  editorData,
  loadChildren,
  onOpenChange,
}: TemplateVariableDialogProps) {
  const outputDefinition =
    group === "output" || variable?.variable_kind === "output";
  const [inputMode, setInputMode] = useState(
    variable?.variable_role === "step_binding"
      ? variable.binding_kind === "unbound"
        ? "unbound"
        : "step_variable"
      : "value",
  );
  const [referenceId, setReferenceId] = useState(
    variable?.binding_source_variable_id ?? "",
  );
  const [referencePath, setReferencePath] = useState(
    variable?.binding_path ?? "",
  );
  const [referenceError, setReferenceError] = useState<string>();
  const steps = useQuery({
    ...templateBuilderQueries.steps(target.templateId),
    enabled:
      open && target.level === "analysisstep" && inputMode === "step_variable",
  });
  const mutations = useTemplateVariableMutations(target);
  const [failures, setFailures] = useState<VariableHierarchyFailure[]>([]);
  const [savedDuringAttempt, setSavedDuringAttempt] = useState<Variable[]>([]);
  const { watch, setValue, handleSubmit } = useForm<VariableDialogForm>({
    defaultValues: {
      field: createInputFieldEditorRow(
        variable
          ? variableDefinition(variable)
          : outputDefinition
            ? {
                key: "",
                type: "text",
                variableKind: "output",
                required: false,
                order: variables.length,
              }
            : undefined,
      ),
    },
  });
  const field = watch("field") as InputFieldEditorRow;
  const sourceGroups =
    target.level === "analysisstep"
      ? getStepVariableSources(
          steps.data ?? [],
          target.stepId,
          undefined,
          field.type,
        )
      : [];
  const referenceSource = sourceGroups
    .flatMap((group) => [...group.inputs, ...group.outputs])
    .find((source) => source.id === referenceId);
  const source = useMemo(
    () =>
      selectableVariables.find(
        (candidate) => candidate.variable_id === field.sourceVariableId,
      ),
    [field.sourceVariableId, selectableVariables],
  );
  const linkedTable =
    field.variableLinkedTable ?? source?.variable_linked_table ?? null;
  const { data: linkedAnalyses = [], isLoading: linkedAnalysesLoading } =
    useQuery({
      ...templateQueries.linkedAnalyses(),
      enabled: open && linkedTable === "analysis",
    });
  const {
    data: linkedAnalysisSteps = [],
    isLoading: linkedAnalysisStepsLoading,
  } = useQuery({
    ...templateQueries.linkedAnalysisSteps(target.templateId),
    enabled: open && linkedTable === "analysisstep",
  });
  const resolvedEditorData = {
    ...editorData,
    linkedAnalyses,
    linkedAnalysisSteps,
    isLoading:
      editorData.isLoading ||
      linkedAnalysesLoading ||
      linkedAnalysisStepsLoading,
  };
  const isPending =
    mutations.createVariable.isPending ||
    mutations.createVariableHierarchy.isPending ||
    mutations.updateVariable.isPending;

  const save = handleSubmit(async ({ field: rawDraftField }) => {
    const draftField = rawDraftField as InputFieldEditorRow;
    setFailures([]);
    const definition = inputFieldEditorRowToDefinition(draftField);
    if (outputDefinition) {
      definition.variableKind = "output";
      definition.required = false;
      definition.defaultValue = undefined;
    }
    setReferenceError(undefined);
    if (
      inputMode === "step_variable" &&
      (!referenceSource || !isJsonPointer(referencePath))
    ) {
      setReferenceError(
        !referenceSource
          ? "Select an available earlier-section variable."
          : "Enter a valid JSON Pointer.",
      );
      return;
    }
    if (
      inputMode === "step_variable" &&
      referenceSource?.requiresProjection &&
      !referencePath
    ) {
      setReferenceError(
        "This source requires a projection. Choose a typed output or enter a JSON Pointer.",
      );
      return;
    }
    const atomicInput = toAtomicInput(definition);
    if (
      target.level === "analysisstep" &&
      !outputDefinition &&
      (inputMode !== "value" || variable?.variable_role === "step_binding")
    ) {
      Object.assign(atomicInput, {
        variable_role: inputMode === "value" ? "ordinary" : "step_binding",
        binding_kind: inputMode === "value" ? null : inputMode,
        binding_source_variable_id:
          inputMode === "step_variable" ? referenceId : null,
        binding_path:
          inputMode === "step_variable" ? referencePath || null : null,
        binding_template_id: null,
        binding_source_step_id: null,
        binding_target_variable_id: null,
        ...(inputMode !== "value"
          ? {
              variable_default: null,
              source_variable_id: null,
              variable_kind: "input",
            }
          : {}),
      });
    }
    if (!variable && inputMode !== "value") {
      try {
        await mutations.createVariable.mutateAsync({ variable: atomicInput });
        onOpenChange(false);
      } catch (error) {
        setReferenceError(getErrorMessage(error, "Could not save variable"));
      }
      return;
    }
    let currentVariables = [...variables, ...savedDuringAttempt];
    let canonicalVariable = variable;

    if (variable) {
      try {
        canonicalVariable = await mutations.updateVariable.mutateAsync({
          variableId: variable.variable_id,
          variable: atomicInput,
        });
      } catch (error) {
        setFailures([
          {
            sourceVariableId:
              variable.source_variable_id ?? variable.variable_id,
            name: variable.variable_name_frontend ?? variable.variable_name,
            error,
          },
        ]);
        return;
      }
      if (inputMode !== "value" || variable.variable_role === "step_binding") {
        onOpenChange(false);
        return;
      }
      currentVariables = [
        ...currentVariables.filter(
          (item) => item.variable_id !== canonicalVariable?.variable_id,
        ),
        canonicalVariable,
      ];
    }

    const result = await saveVariableHierarchy({
      target,
      draft: {
        ...definition,
        variableId: canonicalVariable?.variable_id,
      },
      source,
      existing: currentVariables,
      loadChildren,
      createVariable: (input) =>
        mutations.createVariable.mutateAsync({ variable: input }),
      createHierarchy: (input) =>
        mutations.createVariableHierarchy.mutateAsync({ variable: input }),
    });
    setSavedDuringAttempt(result.saved);
    setFailures(result.failed);
    if (result.failed.length === 0) onOpenChange(false);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto overflow-x-hidden sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {variable ? "Edit variable" : "Add variable"}
          </DialogTitle>
          <DialogDescription>
            Changes are sent only after you press Save.
          </DialogDescription>
        </DialogHeader>
        {outputDefinition && <p>Output definition</p>}
        {target.level === "analysisstep" &&
          !outputDefinition &&
          (!variable || variable.variable_kind === "input") && (
            <TemplateVariableReferenceField
              label="Input source"
              value={inputMode}
              options={[
                { id: "value", name: "Value or parent input" },
                { id: "step_variable", name: "Earlier-section variable" },
                { id: "unbound", name: "Unbound (incomplete)" },
              ]}
              onChange={(mode) => {
                setInputMode(mode);
                if (mode !== "value")
                  setValue("field", {
                    ...field,
                    source: "custom",
                    sourceVariableId: undefined,
                  });
              }}
            />
          )}
        {inputMode === "unbound" && (
          <p role="status">Incomplete: choose a source or value.</p>
        )}
        {inputMode === "step_variable" && (
          <div className="space-y-2">
            <TemplateVariableReferenceField
              label="Source variable"
              value={referenceId}
              options={sourceGroups.flatMap((group) =>
                [...group.inputs, ...group.outputs].map((source) => ({
                  id: source.id,
                  name: `${group.stepName} → ${source.direction === "input" ? "Input" : "Output"} → ${source.variable.variable_name_frontend || source.variable.variable_name} (${source.type})`,
                })),
              )}
              onChange={(id) => {
                setReferenceId(id);
                setReferencePath("");
              }}
            />
            <Label htmlFor="ordinary-projection">
              Optional projection (JSON Pointer)
            </Label>
            <Input
              id="ordinary-projection"
              value={referencePath}
              onChange={(event) => setReferencePath(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Leave empty to reference the whole value.
            </p>
            {referenceSource &&
              getStepVariableBindingReadiness(
                referenceSource,
                referencePath,
                field.type,
              ) === "incomplete" && (
                <p role="status">
                  Incomplete: this source or projection has no verified value
                  type. Declare a typed source output.
                </p>
              )}
          </div>
        )}
        {referenceError && <p role="alert">{referenceError}</p>}
        {outputDefinition && (
          <>
            <Label htmlFor="output-order">Order</Label>
            <Input
              id="output-order"
              type="number"
              min="0"
              step="1"
              value={field.order ?? 0}
              onChange={(event) =>
                setValue("field", {
                  ...field,
                  order: Number(event.target.value),
                })
              }
            />
          </>
        )}
        <TemplateInputFieldEditorRow
          outputDefinition={outputDefinition}
          hideValue={inputMode !== "value"}
          hideSource={inputMode !== "value"}
          field={field}
          index={0}
          variableScope={target.level === "analysis" ? "catalog" : "analysis"}
          analysisId={target.templateId}
          data={resolvedEditorData}
          isDraft
          layout="form"
          onChange={(_index, nextField) =>
            setValue(
              "field",
              { ...nextField, tier: "standard" },
              { shouldDirty: true },
            )
          }
        />
        {failures.length > 0 && (
          <div
            role="alert"
            className="rounded-md border border-destructive/40 p-3 text-sm text-destructive"
          >
            <p>Could not save:</p>
            <ul className="list-disc pl-5">
              {failures.map((failure) => (
                <li key={failure.sourceVariableId}>
                  {failure.name}:{" "}
                  <span>
                    {getErrorMessage(failure.error, "Could not save variable")}
                  </span>
                </li>
              ))}
            </ul>
            <p>
              Successful variables were kept. Retry saves only the missing
              items.
            </p>
          </div>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={isPending}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!canSave(field) || isPending}
            onClick={() => void save()}
          >
            {isPending
              ? "Saving…"
              : failures.length > 0
                ? "Retry missing"
                : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
